import { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Box, Checkbox, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  LinearProgress, MenuItem, TextField, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson, meldFout } from '../../utils/apiFout';
import { bevestig } from '../../utils/bevestig';
import { T, stitchDialogPaper, stitchGradientBtn, stitchInputSx, stitchOutlineBtn } from '../ProjectOverview/parts/stitch';

/**
 * Conceptscript (scriptgenerator fase 2): filmtype + lengte + aanwijzingen → een concept V1 uit de
 * projectbronnen. Het werk loopt op de achtergrond; dit venster leest de voortgang. Het resultaat staat in
 * de tool (bewerkbaar) en als Word-export in Documenten. Rustig: één accentknop per stap, de bronnen en
 * de extra keuze staan ingeklapt.
 */
const API = `${BACKEND_URL}/Api/scriptgenerator`;
const POLL_MS = 2500;
const json = { 'Content-Type': 'application/json' };

const STAND = { gevonden: 'gebruikt', leeg: 'ontbreekt', fout: 'niet te lezen', niet_zichtbaar: 'niet gebruikt', weggelaten_eu: 'weggelaten' };

const lezenSx = {
  fontFamily: T.fontBody, fontSize: '0.875rem', color: T.text, lineHeight: 1.7,
  '& p': { m: 0, mb: 1 }, '& ul, & ol': { my: 0.5, pl: 2.5 }, '& li': { mb: 0.3 },
  '& h1': { fontFamily: T.font, fontWeight: 800, fontSize: '1.15rem', mt: 0, mb: 1 },
  '& h2': { fontFamily: T.font, fontWeight: 800, fontSize: '1rem', mt: 3, mb: 1 },
  '& h3, & h4': { fontFamily: T.font, fontWeight: 700, fontSize: '0.92rem', mt: 2, mb: 0.5 },
  '& blockquote': { m: 0, mb: 1.5, pl: 1.5, borderLeft: `3px solid ${T.outlineVariant}`, color: T.secondary },
};

/** Taaknaam uit elke taakvorm (Airtable-record of platte taak). */
export function taakNaamVan(t) {
  const n = t?.fields?.NaamTaak ?? t?.name ?? t?.naam ?? '';
  return Array.isArray(n) ? n.join(' ') : String(n || '');
}

/** "Script V1" (ook "Script V1 - Projectfilm …"), niet V2/DEF. */
export function isScriptV1(t) {
  return /^\s*script\s*v\s?1\b/i.test(taakNaamVan(t));
}

export function minuten(sec) {
  if (!sec) return '';
  return sec < 120 ? `${sec} seconden` : `${Math.round(sec / 6) / 10} minuten`;
}

function Knop({ children, onClick, hoofd = false, disabled = false }) {
  return (
    <Box component="button" type="button" onClick={onClick} disabled={disabled}
      sx={{ ...(hoofd ? stitchGradientBtn : stitchOutlineBtn), cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1 }}>
      {children}
    </Box>
  );
}

function StilLink({ children, onClick }) {
  return (
    <Box component="button" type="button" onClick={onClick}
      sx={{ border: 0, background: 'none', p: 0, cursor: 'pointer', color: T.primary, fontFamily: T.fontBody, fontSize: '0.8rem', textDecoration: 'underline' }}>
      {children}
    </Box>
  );
}

export default function ConceptScriptDialog({ open, onClose, projectId, taakNaam }) {
  const [voorb, setVoorb] = useState(null);          // voorbereiding (filmtypes, voorkeuze, bronnen, eerdere runs)
  const [laadFout, setLaadFout] = useState('');
  const [profiel, setProfiel] = useState('');
  const [lengte, setLengte] = useState('');
  const [aanwijzingen, setAanwijzingen] = useState('');
  const [prive, setPrive] = useState(false);
  const [bronnenOpen, setBronnenOpen] = useState(false);
  const [meerOpen, setMeerOpen] = useState(false);
  const [run, setRun] = useState(null);              // de lopende of geopende run
  const [bewerken, setBewerken] = useState(false);
  const [bewerkRevisie, setBewerkRevisie] = useState(null);   // revisie waarop de bewerking begon (conflictcontrole)
  const [tekst, setTekst] = useState('');
  const [bezig, setBezig] = useState(false);
  const [start, setStart] = useState(null);
  const [nu, setNu] = useState(Date.now());
  const genRef = useRef(0);          // generatie: verhoogd bij openen/sluiten/project — oude antwoorden negeren
  const bewerkenRef = useRef(false);
  useEffect(() => { bewerkenRef.current = bewerken; }, [bewerken]);

  const laadRun = useCallback(async (id) => {
    const gen = genRef.current;
    const r = await authFetch(`${API}/runs/${id}`);
    const d = await leesJson(r, 'Concept laden');
    if (gen !== genRef.current) return null;            // venster dicht of ander project intussen
    setRun(d);
    if (d.status === 'klaar' && !bewerkenRef.current) setTekst(d.uitvoer || '');   // nooit over een bewerking heen
    return d;
  }, []);

  // openen: voorbereiding laden; een lopende run meteen volgen
  useEffect(() => {
    if (!open || !projectId) return undefined;
    genRef.current += 1;
    let weg = false;
    setVoorb(null); setLaadFout(''); setRun(null); setBewerken(false); setPrive(false); setAanwijzingen('');
    (async () => {
      try {
        const r = await authFetch(`${API}/projecten/${projectId}/conceptscript`);
        const d = await leesJson(r, 'Voorbereiden');
        if (weg) return;
        setVoorb(d);
        setProfiel(d.voorkeuze?.type_key || '');
        setLengte(d.lengteSec ? String(d.lengteSec) : '');
        if (d.lopend?.id) { setStart(Date.now()); await laadRun(d.lopend.id); }
      } catch (e) { if (!weg) setLaadFout(e.message); }
    })();
    return () => { weg = true; genRef.current += 1; };
  }, [open, projectId, laadRun]);

  // voortgang volgen zolang de run bezig is, en daarna tot de Word-versie in Documenten staat (of faalt)
  const wachtOpPublicatie = run?.status === 'klaar' && !run.prive && !run.publicatieFout && run.documentRevisie !== run.revisie;
  const volgen = run?.status === 'bezig' || wachtOpPublicatie;
  useEffect(() => {
    if (!open || !volgen || !run?.id) return undefined;
    const wasBezig = run?.status === 'bezig';
    let bezigMet = false;   // geen overlappende verzoeken
    let gestopt = false;
    const t = setInterval(async () => {
      setNu(Date.now());
      if (bezigMet) return;
      bezigMet = true;
      try {
        const d = await laadRun(run.id);
        if (!gestopt && wasBezig && d?.status === 'klaar') toast.success('Conceptscript klaar');
      } catch { /* volgende poging */ }
      finally { bezigMet = false; }
    }, POLL_MS);
    return () => { gestopt = true; clearInterval(t); };
  }, [open, volgen, run?.status, run?.id, laadRun]);

  const maak = async () => {
    if (!profiel) { toast.error('Kies een filmtype'); return; }
    setBezig(true);
    try {
      const r = await authFetch(`${API}/projecten/${projectId}/conceptscript`, {
        method: 'POST', headers: json,
        body: JSON.stringify({ profiel, lengteSec: lengte ? Number(lengte) : null, aanwijzingen, priveBronnen: prive }),
      });
      if (r.status === 409) {
        const d = await r.clone().json().catch(() => ({}));
        if (d?.detail?.runId) { setStart(Date.now()); await laadRun(d.detail.runId); return; }
      }
      const d = await leesJson(r, 'Concept starten');
      setStart(Date.now());
      setRun(d);
    } catch (e) { meldFout(e, 'Concept starten'); }
    finally { setBezig(false); }
  };

  const opslaan = async () => {
    setBezig(true);
    try {
      const r = await authFetch(`${API}/runs/${run.id}`, { method: 'PUT', headers: json, body: JSON.stringify({ uitvoer: tekst, revisie: bewerkRevisie ?? run.revisie }) });
      const d = await leesJson(r, 'Opslaan');
      setRun(d); setTekst(d.uitvoer || ''); setBewerken(false); setBewerkRevisie(null);
      toast.success(d.prive ? 'Opgeslagen (alleen voor jou)' : 'Opgeslagen — de Word-versie in Documenten wordt bijgewerkt');
    } catch (e) { meldFout(e, 'Opslaan'); }
    finally { setBezig(false); }
  };

  const downloadWord = async () => {
    try {
      const r = await authFetch(`${API}/runs/${run.id}/word`);
      if (!r.ok) await leesJson(r, 'Word maken');
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${voorb?.project?.nummer || 'project'} - Script V1 (CONCEPT).docx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    } catch (e) { meldFout(e, 'Word maken'); }
  };

  const alsVoorbeeld = async () => {
    const ok = await bevestig({
      titel: 'Als goed voorbeeld opslaan?',
      tekst: 'Doe dit alleen als de klant dit script heeft goedgekeurd. De huidige versie wordt een goed voorbeeld voor dit filmtype en de generator leert er bij volgende scripts van.',
      knop: 'Opslaan als voorbeeld',
    });
    if (!ok) return;
    try {
      const r = await authFetch(`${API}/runs/${run.id}/voorbeeld`, { method: 'POST', headers: json, body: JSON.stringify({ revisie: run.revisie }) });
      setRun(await leesJson(r, 'Voorbeeld opslaan'));
      toast.success('Opgeslagen als goed voorbeeld (Admin → Scriptstijlen)');
    } catch (e) { meldFout(e, 'Voorbeeld opslaan'); }
  };

  const naarDocumenten = async () => {
    if (run.prive) {
      const ok = await bevestig({
        titel: 'In Documenten zetten?',
        tekst: 'Dit concept gebruikt ook jouw eigen transcripten of mails. In Documenten kan het hele team het lezen.',
        knop: 'Ja, team mag het zien',
      });
      if (!ok) return;
    }
    try {
      const r = await authFetch(`${API}/runs/${run.id}/openbaar`, { method: 'POST', headers: json, body: JSON.stringify({ bevestigd: true }) });
      setRun(await leesJson(r, 'In Documenten zetten'));
      toast.success('Staat in Documenten');
    } catch (e) { meldFout(e, 'In Documenten zetten'); }
  };

  const nieuw = () => { setRun(null); setBewerken(false); };
  // sluiten met niet-opgeslagen wijzigingen: eerst vragen (vangnet)
  const sluit = async () => {
    if (bewerken && run && tekst !== (run.uitvoer || '')) {
      const ok = await bevestig({ titel: 'Wijzigingen weggooien?', tekst: 'Je hebt het script aangepast maar nog niet opgeslagen.', knop: 'Weggooien', gevaarlijk: true });
      if (!ok) return;
    }
    onClose?.();
  };
  const status = run?.status;
  const gevonden = (voorb?.bronnen || []).filter((b) => b.status === 'gevonden').length;
  const totaal = (voorb?.bronnen || []).length;
  const eerdere = (voorb?.runs || []).filter((r) => r.status === 'klaar');
  const voorkeuzeReden = voorb?.voorkeuze?.reden;
  const seconden = start ? Math.round((nu - start) / 1000) : 0;

  return (
    <Dialog open={open} onClose={sluit} maxWidth="md" fullWidth PaperProps={{ sx: stitchDialogPaper }}>
      <DialogTitle sx={{ fontFamily: T.font, fontWeight: 800 }}>
        Conceptscript
        <Typography component="div" sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: T.outline }}>
          {voorb?.project ? `${voorb.project.nummer} · ${voorb.project.naam}` : ' '}{taakNaam ? ` — voor ${taakNaam}` : ''}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {laadFout && <Typography sx={{ color: T.error, fontFamily: T.fontBody }}>{laadFout}</Typography>}
        {!voorb && !laadFout && <LinearProgress sx={{ my: 2 }} />}

        {/* 1. kiezen */}
        {voorb && !run && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box>
              <TextField select fullWidth size="small" label="Filmtype" value={profiel} onChange={(e) => setProfiel(e.target.value)} sx={stitchInputSx}>
                {voorb.profielen.map((p) => <MenuItem key={p.typeKey} value={p.typeKey}>{p.naam}</MenuItem>)}
              </TextField>
              {voorkeuzeReden && (
                <Typography sx={{ mt: 0.5, fontFamily: T.fontBody, fontSize: '0.75rem', color: T.outline }}>
                  {voorb.voorkeuze?.zeker ? `Voorgekozen op ${voorkeuzeReden}.` : 'Niet zeker welk type past — kies zelf.'}
                </Typography>
              )}
            </Box>
            <TextField size="small" label="Lengte (seconden)" type="number" value={lengte} onChange={(e) => setLengte(e.target.value)}
              helperText={voorb.lengteSec ? `${minuten(voorb.lengteSec)} volgens de ${voorb.lengteBron}` : 'Niet gevonden in offerte of deliverables'}
              sx={{ ...stitchInputSx, maxWidth: 260 }} inputProps={{ min: 10, max: 1800 }} />
            <TextField multiline minRows={3} label="Aanwijzingen (optioneel)" value={aanwijzingen} onChange={(e) => setAanwijzingen(e.target.value)}
              placeholder="Bijvoorbeeld: zelfde toon als de vorige film, en laat de onderzoeker het verhaal dragen." sx={stitchInputSx} inputProps={{ maxLength: 2000 }} />

            <Box>
              <StilLink onClick={() => setBronnenOpen((x) => !x)}>
                {`Bronnen: ${gevonden} van ${totaal} gevonden`}{voorb.schets ? ' — te weinig voor een uitgeschreven script, het wordt een schets' : ''}
              </StilLink>
              <Collapse in={bronnenOpen}>
                <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5, fontFamily: T.fontBody, fontSize: '0.8rem', color: T.secondary }}>
                  {voorb.bronnen.map((b) => (
                    <li key={b.soort}>
                      <strong>{b.label}</strong>: {b.status === 'gevonden' ? `${b.aantal}` : STAND[b.status] || b.status}
                      {b.toelichting ? ` — ${b.toelichting}` : ''}
                    </li>
                  ))}
                </Box>
              </Collapse>
            </Box>

            <Box>
              <StilLink onClick={() => setMeerOpen((x) => !x)}>Meer opties</StilLink>
              <Collapse in={meerOpen}>
                <FormControlLabel sx={{ mt: 1 }} control={<Checkbox size="small" checked={prive} onChange={(e) => setPrive(e.target.checked)} />}
                  label={<Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem' }}>Ook mijn eigen transcripten en mails gebruiken (het concept is dan alleen voor jou)</Typography>} />
              </Collapse>
            </Box>

            {eerdere.length > 0 && (
              <Box sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: T.secondary }}>
                Eerdere concepten:{' '}
                {eerdere.slice(0, 3).map((r, i) => (
                  <span key={r.id}>{i > 0 ? ' · ' : ''}<StilLink onClick={() => laadRun(r.id).catch((e) => meldFout(e, 'Openen'))}>
                    {new Date(r.klaarOp || r.gestartOp).toLocaleDateString('nl-NL')} ({r.door?.split('@')[0]})</StilLink></span>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* 2. bezig */}
        {status === 'bezig' && (
          <Box sx={{ py: 3 }} aria-live="polite">
            <Typography sx={{ fontFamily: T.font, fontWeight: 700, mb: 1.5 }}>{run.stap || 'Bezig'}</Typography>
            <LinearProgress />
            <Typography sx={{ mt: 1.5, fontFamily: T.fontBody, fontSize: '0.8rem', color: T.outline }}>
              {seconden > 0 ? `${seconden} s bezig. ` : ''}Je kunt dit venster sluiten; het concept wordt verder gemaakt en staat straks in Documenten.
            </Typography>
          </Box>
        )}

        {/* 3. fout of geblokkeerd */}
        {(status === 'fout' || status === 'geblokkeerd') && (
          <Box sx={{ py: 2 }}>
            <Typography sx={{ fontFamily: T.font, fontWeight: 700, mb: 1 }}>
              {status === 'geblokkeerd' ? 'Niet gemaakt' : 'Er ging iets mis'}
            </Typography>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.875rem', color: T.secondary }}>{run.fout}</Typography>
          </Box>
        )}

        {/* 4. klaar: lezen of bewerken */}
        {status === 'klaar' && (
          bewerken ? (
            <TextField multiline fullWidth minRows={22} value={tekst} onChange={(e) => setTekst(e.target.value)}
              sx={{ ...stitchInputSx, '& textarea': { fontFamily: 'ui-monospace, Menlo, monospace', fontSize: '0.8rem', lineHeight: 1.6 } }} />
          ) : (
            <Box sx={lezenSx}><ReactMarkdown>{tekst}</ReactMarkdown></Box>
          )
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
        {status === 'klaar' && (
          <Typography sx={{ flex: 1, fontFamily: T.fontBody, fontSize: '0.75rem', color: T.outline }}>
            {run.prive ? 'Alleen voor jou (gebruikt je eigen transcripten of mails).'
              : run.publicatieFout ? <>{run.publicatieFout} <StilLink onClick={naarDocumenten}>Opnieuw proberen</StilLink></>
                : run.documentId && run.documentRevisie === run.revisie ? 'Word-versie staat in Documenten. Wijzigingen in Word komen niet terug in de tool.'
                  : 'De Word-versie wordt in Documenten gezet.'}
          </Typography>
        )}
        {!run && voorb && <Knop hoofd onClick={maak} disabled={bezig || !profiel}>Maak concept</Knop>}
        {(status === 'fout' || status === 'geblokkeerd') && <Knop onClick={nieuw}>Opnieuw</Knop>}
        {status === 'klaar' && !bewerken && (
          <>
            <StilLink onClick={nieuw}>Nieuw concept</StilLink>
            {!run.voorbeeldId && !run.prive && <StilLink onClick={alsVoorbeeld}>Goedgekeurd: als goed voorbeeld opslaan</StilLink>}
            {run.voorbeeldId && <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', color: T.outline }}>Staat als voorbeeld opgeslagen</Typography>}
            {run.prive && <Knop onClick={naarDocumenten}>In Documenten zetten</Knop>}
            <Knop onClick={downloadWord}>Word</Knop>
            <Knop hoofd onClick={() => { setTekst(run.uitvoer || ''); setBewerkRevisie(run.revisie); setBewerken(true); }}>Bewerken</Knop>
          </>
        )}
        {status === 'klaar' && bewerken && (
          <>
            <Knop onClick={() => { setTekst(run.uitvoer || ''); setBewerken(false); setBewerkRevisie(null); }}>Annuleren</Knop>
            <Knop hoofd onClick={opslaan} disabled={bezig}>Opslaan</Knop>
          </>
        )}
        <Knop onClick={sluit}>Sluiten</Knop>
      </DialogActions>
    </Dialog>
  );
}
